// Full-screen placement chrome — Instagram Reels and Stories.
//
// These placements draw their UI ON the creative, so everything here is
// absolutely positioned inside the media box and never consumes layout.
// pointer-events are off throughout so native <video> controls stay
// clickable underneath.
//
// Geometry is in `u` = 1% of media width (u = 10.8px on a 1080-wide
// creative), reverse-engineered from current iOS builds. Meta publishes
// only qualitative safe zones plus the Ads Manager guardrail, so treat
// these as a close approximation, not a spec — re-calibrate against
// native screenshots if pixel-parity ever matters.

import type { CSSProperties } from 'react';
import { Icon, Avatar } from './icons';
import type { ChromeDensity, ChromeIdentity } from './types';

const FONT = "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const LOREM = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt';

const shadowText: CSSProperties = { textShadow: '0 1px 2px rgba(0,0,0,.6)' };

const overlayBase: CSSProperties = {
  position: 'absolute', inset: 0, pointerEvents: 'none', color: '#fff',
  fontFamily: FONT, overflow: 'hidden', zIndex: 5
};

type OverlayProps = {
  identity:        ChromeIdentity;
  u:               number;
  density:         ChromeDensity;
  showSponsored?:  boolean;
  showEngagement?: boolean;
};

// Strips scheme/www and any path so the sticker shows a bare host the
// way Instagram renders it.
export function displayDomain(input?: string | null): string {
  const raw = String(input || '').trim();
  if (!raw) return 'loremipsum.com';
  try {
    const url = new URL(raw.includes('://') ? raw : `https://${raw}`);
    return url.hostname.replace(/^www\./i, '').toLowerCase();
  } catch {
    return raw.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].toLowerCase() || 'loremipsum.com';
  }
}

// ── Instagram Reels ──────────────────────────────────────────────────
// Bottom scrim, right-hand action rail, and the caption + CTA stack.
// The rail sits above the caption block rather than beside it, which is
// why the caption is inset on the right by ~14u.

export function ReelsChrome({
  identity, u, density, showSponsored = true, showEngagement = true
}: OverlayProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop now';

  const railItems: Array<[string, string]> = compact
    ? [['heart', ''], ['comment', ''], ['send', '']]
    : [['heart', '12.4K'], ['comment', '342'], ['send', ''], ['bookmark', ''], ['dots', '']];

  return (
    <div style={overlayBase} aria-hidden>
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '42%',
        background: 'linear-gradient(transparent, rgba(0,0,0,.62))'
      }} />

      {/* Right action rail — clears the caption/CTA stack below it. */}
      {showEngagement && (
        <div style={{
          position: 'absolute', right: u * 4, bottom: u * (compact ? 16 : 21),
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: u * (compact ? 4.4 : 5.2)
        }}>
          {railItems.map(([name, count]) => (
            <div key={name} style={{ textAlign: 'center' }}>
              <Icon name={name} size={u * 7.4} shadow />
              {count && (
                <b style={{ display: 'block', fontSize: u * 2.9, fontWeight: 600, marginTop: u * 0.8, ...shadowText }}>
                  {count}
                </b>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Caption + CTA stack. */}
      <div style={{
        position: 'absolute', left: u * 4, right: u * 14, bottom: u * 4,
        display: 'flex', flexDirection: 'column', gap: u * 2.6
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: u * 2.2, fontSize: u * 3.6, fontWeight: 700, ...shadowText }}>
          <Avatar logoUrl={identity.logoUrl} name={identity.name} size={u * 8.6} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{identity.handle}</span>
        </div>
        {showSponsored && !compact && (
          <div style={{ fontSize: u * 3, fontWeight: 500, opacity: 0.92, ...shadowText }}>Sponsored</div>
        )}
        {showEngagement && !compact && (
          <div style={{ fontSize: u * 3.3, lineHeight: 1.35, opacity: 0.96, ...shadowText }}>
            {LOREM.slice(0, 84)}&hellip; <b style={{ fontWeight: 600, opacity: 0.8 }}>more</b>
          </div>
        )}
        {/* CTA bar — full width of the caption column, ~10u tall. */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'rgba(255,255,255,.94)', color: '#111', borderRadius: u * 1.8,
          padding: `${u * 3.4}px ${u * 3.2}px`, fontSize: u * 3.7, fontWeight: 700
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cta}</span>
          <Icon name="chevR" size={u * 4} style={{ stroke: '#111' }} />
        </div>
        {!compact && (
          <div style={{ display: 'flex', alignItems: 'center', gap: u * 1.8, fontSize: u * 3.1, ...shadowText }}>
            <Icon name="music" size={u * 3.2} shadow />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {identity.name} &middot; Original audio
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Instagram Stories ────────────────────────────────────────────────
// Progress segments, creator chip, the link sticker, and the bottom CTA.

export function StoriesChrome({
  identity, u, density, showSponsored = true
}: OverlayProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop now';

  return (
    <div style={overlayBase} aria-hidden>
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 0, height: '18%',
        background: 'linear-gradient(rgba(0,0,0,.5), transparent)'
      }} />
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '24%',
        background: 'linear-gradient(transparent, rgba(0,0,0,.62))'
      }} />

      {/* Progress segments. */}
      <div style={{ position: 'absolute', top: u * 2, left: u * 2, right: u * 2, display: 'flex', gap: u * 0.8 }}>
        <i style={{ flex: 1, height: u * 0.65, borderRadius: u * 0.45, background: '#fff' }} />
        <i style={{ flex: 1, height: u * 0.65, borderRadius: u * 0.45, background: 'rgba(255,255,255,.35)' }} />
      </div>

      {/* Creator chip. */}
      <div style={{
        position: 'absolute', top: u * 4.6, left: u * 3, right: u * 3,
        display: 'flex', alignItems: 'center', gap: u * 2.2, fontSize: u * 3.4, fontWeight: 700, ...shadowText
      }}>
        <Avatar logoUrl={identity.logoUrl} name={identity.name} size={u * 8.6} ring />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{identity.handle}</span>
        {showSponsored && !compact && <span style={{ fontWeight: 400, fontSize: u * 3, opacity: 0.9, flex: 'none' }}>Sponsored</span>}
        <span style={{ marginLeft: 'auto', flex: 'none' }}><Icon name="x" size={u * 6} shadow /></span>
      </div>

      {/* Link sticker + CTA. The sticker is the chain-link pill
          Instagram overlays when a Story carries a destination — the
          one that reads as an odd little emoji rather than a styled
          hyperlink. Sits above the CTA, left-of-centre, the way the
          sticker tray drops it by default. */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: u * 4,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: u * 2.6
      }}>
        <LinkSticker domain={displayDomain(identity.linkDomain)} u={u} compact={compact} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: u * 1.6 }}>
          <Icon name="chevU" size={u * 5} shadow />
          <b style={{
            background: '#fff', color: '#111', borderRadius: u * 5,
            padding: `${u * 2.6}px ${u * 5.5}px`, fontSize: u * 3.6, fontWeight: 700,
            maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {cta}
          </b>
        </div>
      </div>
    </div>
  );
}

// Instagram's link sticker: a squat white rounded-rect with the chain
// glyph and the bare host in medium-weight dark text. Slight rotation
// is deliberate — stickers land off-axis unless the creator squares
// them up, and that tilt is a big part of why it reads as a sticker.
function LinkSticker({ domain, u, compact }: { domain: string; u: number; compact: boolean }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: u * 1.4,
      background: '#fff', color: '#111',
      borderRadius: u * 1.6,
      padding: `${u * 1.6}px ${u * 2.6}px`,
      fontSize: u * (compact ? 3.2 : 3.1),
      fontWeight: 600,
      letterSpacing: '-0.01em',
      boxShadow: '0 1px 3px rgba(0,0,0,.25)',
      transform: 'rotate(-2deg)',
      maxWidth: '78%'
    }}>
      <Icon name="link" size={u * 3.6} style={{ stroke: '#111', strokeWidth: 2.2 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{domain}</span>
    </div>
  );
}
